import { useEffect, useRef, useState } from "react";
import { useUnits } from "@/context/UnitsContext";

// Field -> metric multipliers per quantity (metric -> field divides).
const TO_METRIC = {
  pressure: 1 / 14.5038, // psi -> bar
  density: 119.826, // ppg -> kg/m3
  length: 0.3048, // ft -> m
  diameter: 25.4, // in -> mm
  volume: 158.987, // bbl -> L
  capacity: 521.61, // bbl/ft -> L/m
  dtemp: 5 / 9, // temperature difference °F -> °C
};

export function convertValue(value, kind, from, to) {
  const n = parseFloat(value);
  if (from === to || !TO_METRIC[kind] || !Number.isFinite(n)) return value;
  const f = TO_METRIC[kind];
  return String(parseFloat((to === "metric" ? n * f : n / f).toPrecision(5)));
}

const convertAll = (obj, kinds, from, to) =>
  Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, kinds[k] ? convertValue(v, kinds[k], from, to) : v]));

// Input state whose unit-aware fields convert when the Field/Metric toggle changes.
// `fieldDefaults` are always given in field units.
export function useUnitInputs(fieldDefaults, kinds) {
  const { units } = useUnits();
  const [state, setState] = useState(() => convertAll(fieldDefaults, kinds, "field", units));
  const prev = useRef(units);
  useEffect(() => {
    if (prev.current === units) return;
    const from = prev.current;
    prev.current = units;
    setState((s) => convertAll(s, kinds, from, units));
    // kinds is a static per-card map
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [units]);
  return [state, setState];
}

// Runs `fn` when the units toggle changes (not on first render) — used to clear stale results.
export function useOnUnitsChange(fn) {
  const { units } = useUnits();
  const first = useRef(units);
  const cb = useRef(fn);
  cb.current = fn;
  useEffect(() => {
    if (first.current === units) return;
    first.current = units;
    cb.current();
  }, [units]);
}
